import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLeague } from '@/contexts/LeagueContext';
import { DraftPickLog, DraftState } from '@/types/draftPlayer';

interface DraftOrder {
  id: string;
  team_id: string;
  round: number;
  pick_number: number;
  round_pick: number;
}

interface DbDraft {
  id: string;
  league_id: string;
  status: string;
  draft_type: string;
  rounds: number;
  seconds_per_pick: number;
  hours_per_pick: number;
  pause_overnight: boolean;
  pause_start_hour: number;
  pause_end_hour: number;
  paused_remaining_seconds: number | null;
  current_round: number;
  current_pick: number;
  current_team_id: string | null;
  pick_deadline_at: string | null;
  started_at: string | null;
  paused_at: string | null;
  scheduled_for: string | null;
}

interface DbDraftPick {
  id: string;
  draft_id: string;
  team_id: string;
  player_id: string;
  round: number;
  pick_number: number;
  round_pick: number;
  is_auto_pick: boolean;
  picked_by_commissioner: boolean;
  picked_at: string;
}

interface DraftAppliedSabotage {
  id: string;
  draft_id: string;
  applied_by_team_id: string;
  target_team_id: string;
  sabotage_name: string;
  sabotage_icon: string;
  effect_type: string;
  effect_config: any;
  status: string;
  applied_at_pick: number;
  expires_at_pick: number | null;
}

export const useLiveDraft = () => {
  const { user } = useAuth();
  const { activeLeague, teams } = useLeague();
  const [draft, setDraft] = useState<DbDraft | null>(null);
  const [draftOrder, setDraftOrder] = useState<DraftOrder[]>([]);
  const [picks, setPicks] = useState<DbDraftPick[]>([]);
  const [activeSabotages, setActiveSabotages] = useState<DraftAppliedSabotage[]>([]);
  const [totalSabotageCount, setTotalSabotageCount] = useState(0);
  const [totalSabotageSpend, setTotalSabotageSpend] = useState(0);
  const [sabotagesReceivedCount, setSabotagesReceivedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const channelRef = useRef<any>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // My team in this league
  const myTeam = teams.find(t => t.user_id === user?.id);

  // Fetch draft data for active league
  const fetchDraft = useCallback(async () => {
    if (!activeLeague) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch draft for league
      const { data: draftData } = await supabase
        .from('drafts')
        .select('*')
        .eq('league_id', activeLeague.id)
        .maybeSingle();

      if (!draftData) {
        console.log('[useLiveDraft] No draft found for league:', activeLeague.id);
        setIsLoading(false);
        return;
      }

      console.log('[useLiveDraft] Draft found:', draftData.id, 'status:', draftData.status, 'pick:', draftData.current_pick, 'deadline:', draftData.pick_deadline_at);
      setDraft(draftData as DbDraft);

      // Skip heavy fetches for completed drafts — we only need the draft record itself
      if (draftData.status === 'completed') {
        setIsLoading(false);
        return;
      }

      // Fetch order, picks, and active sabotages in parallel
      const myTeamId = teams.find(t => t.user_id === user?.id)?.id;
      const [orderRes, picksRes, sabotagesRes, receivedRes, totalSabRes] = await Promise.all([
        supabase
          .from('draft_order')
          .select('*')
          .eq('draft_id', draftData.id)
          .order('pick_number'),
        supabase
          .from('draft_picks')
          .select('*')
          .eq('draft_id', draftData.id)
          .order('pick_number'),
        supabase
          .from('draft_applied_sabotages')
          .select('*')
          .eq('draft_id', draftData.id)
          .in('status', ['active', 'pending']),
        // Fetch ALL draft sabotages (for Karma: detect global vs targeted)
        supabase
          .from('draft_applied_sabotages')
          .select('id, applied_by_team_id, target_team_id, sabotage_name, applied_at_pick, effect_type')
          .eq('draft_id', draftData.id)
          .neq('effect_type', 'karma'),
        // Fetch ALL sabotages in this draft (any status) — need cost for spend total
        supabase
          .from('draft_applied_sabotages')
          .select('id, cost')
          .eq('draft_id', draftData.id),
      ]);

      setDraftOrder(orderRes.data || []);
      setPicks(picksRes.data as DbDraftPick[] || []);
      setActiveSabotages((sabotagesRes.data || []) as DraftAppliedSabotage[]);
      const allSabs = totalSabRes.data || [];
      setTotalSabotageCount(allSabs.length);
      setTotalSabotageSpend(allSabs.reduce((sum: number, s: any) => sum + (s.cost || 0), 0));
      // Count non-global targeted sabotages against my team for Karma eligibility
      const allDraftSabs = receivedRes.data || [];
      // Group by sender+name+pick to detect globals (multiple targets = global)
      const groupTargets = new Map<string, Set<string>>();
      for (const s of allDraftSabs) {
        const key = `${s.applied_by_team_id}|${s.sabotage_name}|${s.applied_at_pick}`;
        if (!groupTargets.has(key)) groupTargets.set(key, new Set());
        groupTargets.get(key)!.add(s.target_team_id);
      }
      const globalKeys = new Set<string>();
      for (const [key, targets] of groupTargets) {
        if (targets.size > 1) globalKeys.add(key);
      }
      // Count only sabotages targeting my team that are NOT global
      const nonGlobalCount = allDraftSabs.filter(s => {
        if (s.target_team_id !== myTeamId) return false;
        const key = `${s.applied_by_team_id}|${s.sabotage_name}|${s.applied_at_pick}`;
        return !globalKeys.has(key);
      }).length;
      setSabotagesReceivedCount(nonGlobalCount);
    } catch (err) {
      console.error('Failed to fetch draft:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeLeague, teams, user?.id]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!draft) return;
    if (draft.status === 'completed') return;

    const channel = supabase
      .channel(`draft-${draft.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'drafts', filter: `id=eq.${draft.id}` },
        (payload) => {
          if (payload.new) {
            setDraft(payload.new as DbDraft);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'draft_picks', filter: `draft_id=eq.${draft.id}` },
        (payload) => {
          if (payload.new) {
            setPicks(prev => [...prev, payload.new as DbDraftPick]);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'draft_order', filter: `draft_id=eq.${draft.id}` },
        () => {
          // Refetch order on any change (e.g. Snake Reversal)
          supabase
            .from('draft_order')
            .select('*')
            .eq('draft_id', draft.id)
            .order('pick_number')
            .then(({ data }) => {
              if (data && mountedRef.current) setDraftOrder(data);
            });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'draft_applied_sabotages', filter: `draft_id=eq.${draft.id}` },
        (payload) => {
          console.log('[useLiveDraft] draft_applied_sabotages realtime event:', payload.eventType, payload.new);
          // Refetch sabotages on any change
          supabase
            .from('draft_applied_sabotages')
            .select('*')
            .eq('draft_id', draft.id)
            .in('status', ['active', 'pending'])
            .then(({ data }) => {
              if (!mountedRef.current) return;
              console.log('[useLiveDraft] refetched active sabotages:', data?.length, data?.map((s: any) => `${s.sabotage_name}[${s.status}]`));
              setActiveSabotages((data || []) as DraftAppliedSabotage[]);
            });
          // Also refresh total count + spend
          supabase
            .from('draft_applied_sabotages')
            .select('id, cost')
            .eq('draft_id', draft.id)
            .then(({ data }) => {
              if (!mountedRef.current) return;
              const sabs = data || [];
              setTotalSabotageCount(sabs.length);
              setTotalSabotageSpend(sabs.reduce((sum: number, s: any) => sum + (s.cost || 0), 0));
            });
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [draft?.id]);

  useEffect(() => {
    fetchDraft();
  }, [fetchDraft]);

  // Build pick log from DB picks + team names
  const pickLog: DraftPickLog[] = picks.map(p => {
    const team = teams.find(t => t.id === p.team_id);
    return {
      pickNumber: p.pick_number,
      round: p.round,
      pickInRound: p.round_pick,
      teamId: p.team_id,
      teamName: team?.name || 'Unknown',
      playerId: p.player_id,
      playerName: '', // Will be resolved by the component using player data
      position: '',   // Same
      timestamp: new Date(p.picked_at),
      wasAutoPick: p.is_auto_pick,
      wasCommissionerPick: p.picked_by_commissioner || false,
    };
  });

  // Drafted player IDs (exclude skipped picks which have null player_id)
  const draftedPlayerIds = picks.filter(p => p.player_id).map(p => p.player_id);

  // Skipped pick numbers (from is_skipped flag in actual picks data)
  const skippedPickNumbers = new Set(
    picks.filter(p => (p as any).is_skipped).map(p => p.pick_number)
  );

  // Map of player_id -> team name for drafted player attribution in search
  const draftedByTeamMap = new Map<string, string>();
  for (const p of picks) {
    if (p.player_id) {
      const team = teams.find(t => t.id === p.team_id);
      if (team) draftedByTeamMap.set(p.player_id, team.name);
    }
  }

  // Current on-the-clock team — try draft_order first, then fall back to draft.current_team_id
  const currentOrderEntry = draftOrder.find(o => o.pick_number === (draft?.current_pick || 1));
  const onTheClockTeam = (currentOrderEntry ? teams.find(t => t.id === currentOrderEntry.team_id) : null)
    || (draft?.current_team_id ? teams.find(t => t.id === draft.current_team_id) : null);

  const isMyTurn = !!(myTeam && onTheClockTeam && myTeam.id === onTheClockTeam.id);

  const currentPick = draft?.current_pick || 1;

  // Extract fake injury player IDs from active Injury Report sabotages (respect expires_at_pick)
  const fakeInjuryPlayerIds: string[] = activeSabotages
    .filter(s => s.effect_type === 'fake_injury' && s.status === 'active'
      && (s.expires_at_pick === null || s.expires_at_pick >= currentPick))
    .map(s => s.effect_config?.player_id)
    .filter(Boolean);

  // Guess Who: check if my team is targeted by an active, non-expired guess_who sabotage
  // Use >= so the sabotage stays visible through the pick it's meant to affect
  // (expires_at_pick=N means active during pick N, expires when pick N+1 starts)
  const isGuessWhoActive = !!(myTeam && activeSabotages.some(
    s => s.effect_type === 'guess_who' && s.status === 'active' && s.target_team_id === myTeam.id
      && (s.expires_at_pick === null || s.expires_at_pick >= currentPick)
  ));

  // Karma: find pending karma sabotages (owned by any team, visible to all)
  const karmaSabotages = activeSabotages.filter(
    s => s.effect_type === 'karma' && s.status === 'active'
  );

  // Make a pick via edge function (server-side validation and state advancement)
  const makePick = async (playerId: string) => {
    if (!draft || !myTeam || !isMyTurn) return false;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return false;

      const response = await supabase.functions.invoke('make-pick', {
        body: { draft_id: draft.id, player_id: playerId },
      });

      if (response.error) {
        console.error('Failed to make pick:', response.error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Failed to make pick:', err);
      return false;
    }
  };

  return {
    draft,
    draftOrder,
    picks: pickLog,
    draftedPlayerIds,
    skippedPickNumbers,
    draftedByTeamMap,
    isLoading,
    isMyTurn,
    myTeam,
    onTheClockTeam,
    makePick,
    refetch: fetchDraft,
    hasDraft: !!draft,
    activeSabotages,
    totalSabotageCount,
    totalSabotageSpend,
    fakeInjuryPlayerIds,
    isGuessWhoActive,
    karmaSabotages,
    sabotagesReceivedCount,
  };
};
